import { AnimatedSprite, Container, Sprite, type Texture } from 'pixi.js';
import type { Actor } from '../../battle/types';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../../data/constants';
import { isNightModeEnabled, isSnowModeEnabled } from '../../dev/debugMode';
import type { ArenaBundle, ArenaPlayContext } from '../types';
import {
  breakWall,
  crashBallIntoWall,
  dropBall,
  explodeDroppedBall,
  hasEmbeddedBall,
  resetEmbeddedBalls,
  spawnEmbeddedBall,
} from './EmbeddedBall';
import { CANNONBALL_OBJECT_ID, THE_PIT_INTERACTIONS } from './interactions';
import { Kid } from './Kid';
import { buildMoonBeam } from './Moonlight';
import { NightSky } from './NightSky';
import { Torch } from './Torch';

// Per-torch placement + size. Tweak each torch independently here. `facing: -1`
// flips the flame so it leans away from the wall on the right side. `height` is
// in design-px (logical viewport coords); halo radius scales with it.
const TORCHES = [
  { x: 300, y: 85,  height: 80,  facing: 1 as const },
  { x: 544, y: 85,  height: 80,  facing: -1 as const },
  { x: 30,  y: 115, height: 120, facing: 1 as const },
  { x: 818, y: 115, height: 120, facing: -1 as const },
];

// Spectator kid peeking over the railing. Anchored bottom-center so `y` is the
// railing line his hands grip; `height` is the sprite height in design-px. The
// 4x4 sheet cycles his facial expressions - low speed so it reads as an idle
// reaction, not a flicker. Tweak placement here; behavior lives in Kid.ts.
const KID = { x: 455, y: 251, height: 35 };

// Toggle to show/hide the kid while the railing area is in flux.
const SHOW_KID = false;

// Blurred foreground railing pinned to the bottom corners, in front of the
// fighters. One texture (the left piece), the right side is a mirror. `height`
// in design-px, aspect preserved.
const RAILING_HEIGHT = 100;

// Stone doorway arch laid over the arch painted into arena-top. Anchored
// top-center (x = arch center, y = arch top, design-px); the pillar bases run
// below the wall/floor seam and hide behind the arena-bottom layer. `height`
// in design-px, aspect preserved. Tune to match the painted outline.
const ARCH = { x: 422, y: 46, height: 255 };

// Scene visible through the arch opening (forge room). Drawn under the arch
// frame so the stones cover its edges. Same anchoring as ARCH (top-center,
// height in design-px); tune independently of the frame.
const ARCH_BG = { x: 422, y: 70, height: 235 };
// Forge scene loop behind the arch. On-screen fps = speed * 60 / BATTLE_PACE_SCALE.
const ARCH_BG_ANIM_SPEED = 0.99;
// Kid-style idle: rest on frame 0, play the sheet once after a random
// wall-time pause, rest again.
const ARCH_BG_PAUSE_MIN_MS = 1000;
const ARCH_BG_PAUSE_MAX_MS = 5000;

// Night mode (debug toggle): the warm arena art is dimmed to a cool blue via
// sprite tint (multiplies the art colors - cheap half-darkness without extra
// textures). Torches are skipped; light comes from the moon hole instead.
// Darker = smaller channel values; keep the blue channel leading so the dark
// stays moonlit-cool, not muddy.
const NIGHT_ART_TINT = 0x4a5586;

const ROOT = '/assets/textures/arenas/ThePit';

// Live refs to the mounted scene so a mid-battle wall break (arena_break) can
// flip the arena into night mode: swap the wall texture, kill the torches,
// tint the art, slide the sky in. Module-scoped like the EmbeddedBall
// registry; re-populated on every mount.
type NightRefs = {
  layer: Container;
  top: Sprite;
  // Lit torches with their placement config - a wall break swaps each for the
  // unlit sprite at the same spot.
  torches: { view: Torch; cfg: (typeof TORCHES)[number] }[];
  tintables: Container[];
  night: boolean;
  // Live NightSky instance (null until night is on) - the beam samples its
  // bobOffset every frame to sway with the moon.
  sky: NightSky | null;
};
let nightRefs: NightRefs | null = null;

// Beam callback: current moon bob offset, 0 while night is off.
function moonBobOffset(): number {
  return nightRefs?.sky?.bobOffset ?? 0;
}

// The hole in area_top_hole.png (and the stars in the sky bg) is drawn on the
// RIGHT wall. A break on the LEFT wall (p1's side) mirrors the whole night
// stack around the screen center.
function mirrorAroundCenter(child: Container): Container {
  const group = new Container();
  group.addChild(child);
  group.scale.x = -1;
  group.position.x = DESIGN_WIDTH;
  return group;
}

// Unlit torch (night mode): a static sprite with the same fit/anchor rules as
// the Torch flame, so it lands exactly where the lit one was. torch-off.png
// is drawn as one cell of the torch sheet.
function buildOffTorch(texture: Texture, cfg: (typeof TORCHES)[number]): Sprite {
  const off = new Sprite(texture);
  off.anchor.set(0.5);
  off.scale.set(cfg.height / texture.height);
  off.scale.x *= cfg.facing;
  off.position.set(cfg.x, cfg.y);
  off.tint = NIGHT_ART_TINT;
  return off;
}

function buildNightSky(ctx: Pick<ArenaPlayContext, 'sheets' | 'textures'>): NightSky {
  return new NightSky({
    sky: ctx.textures.nightSkyBg,
    moonFrames: ctx.sheets.moon,
    cloud: ctx.textures.cloud,
    // Debug SNOW toggle rides along for both entry points (NIGHT mount and
    // mid-battle wall break).
    snow: isSnowModeEnabled(),
  });
}

// Mid-battle switch to night, triggered by the wall break. Idempotent: once
// night is on (from a previous break or the debug NIGHT toggle at mount), a
// second break only gets its BAM, the scene stays as is.
function enterNightMode(side: Actor, ctx: ArenaPlayContext): void {
  const refs = nightRefs;
  if (!refs || refs.night) return;
  refs.night = true;
  const flip = side === 'p1';

  // Wall art with the hole replaces the intact wall in place (same layer slot).
  const holeTex = ctx.textures.arenaTopHole;
  if (holeTex) {
    refs.top.texture = holeTex;
    if (flip) {
      refs.top.scale.x = -refs.top.scale.x;
      refs.top.position.x = DESIGN_WIDTH;
    }
  }

  // Sky (bg + moon + clouds) slides in UNDER the wall art so it only shows
  // through the hole.
  const nightSky = buildNightSky(ctx);
  refs.sky = nightSky;
  const sky = flip ? mirrorAroundCenter(nightSky) : nightSky;
  refs.layer.addChildAt(sky, refs.layer.getChildIndex(refs.top));

  // Torches go out: each lit Torch (flame + halo) swaps for the extinguished
  // sprite at the same layer slot, so occlusion by the foreground art stays.
  const offTex = ctx.textures.torchOff;
  for (const { view, cfg } of refs.torches) {
    if (offTex) {
      refs.layer.addChildAt(buildOffTorch(offTex, cfg), refs.layer.getChildIndex(view));
    }
    view.destroy({ children: true });
  }
  refs.torches = [];

  for (const art of refs.tintables) art.tint = NIGHT_ART_TINT;

  // Beam over the floor art (same slot as the mount-time night beam).
  const beam = flip
    ? mirrorAroundCenter(buildMoonBeam(moonBobOffset))
    : buildMoonBeam(moonBobOffset);
  refs.layer.addChild(beam);
}

export const THE_PIT: ArenaBundle = {
  id: 'the_pit',
  name: 'The Pit',
  assets: {
    textures: {
      arenaTop: `${ROOT}/arena-top.png`,
      // Extinguished torch - swapped in for the lit Torch in night mode.
      torchOff: `${ROOT}/torch-off.png`,
      // Same wall with a blown-open hole (right side). Swapped in when a
      // second cannonball breaks the wall, or from mount in debug night mode.
      arenaTopHole: `${ROOT}/area_top_hole.png`,
      // Starry night backdrop laid UNDER the wall - visible through the hole.
      nightSkyBg: `${ROOT}/area-top-hole-bg.png`,
      // Single cloud - NightSky spawns several with random size/alpha/speed.
      cloud: `${ROOT}/cloud.png`,
      arenaBottom: `${ROOT}/arena-bottom.png`,
      // Reused from the cannon skill bundle. Pixi Assets caches by URL so the
      // texture is shared - no double-load cost.
      cannonball: '/assets/textures/characters/sparky_bot/skills/cannon/cannonball.png',
      // "Stuck in wall" pose - the embed sprite swaps to this once the fly-in
      // animation lands. Drawn with cracks / debris so it reads as lodged.
      cannonballInWall: `${ROOT}/cannonball-in-wall.png`,
      // Permanent damage mark left behind when the ball drops out (earthshake).
      // Replaces the lodged ball in the wall and persists for the rest of the
      // battle - cracks/hole where the ball was.
      damagedWall: `${ROOT}/damaged-wall.png`,
      // Stone doorway arch over the painted arch in the back wall.
      arch: `${ROOT}/arch.png`,
      railing: `${ROOT}/railing-left.png`,
    },
    sheets: {
      // Scene behind the arch opening, framed by the arch stones.
      archBg: { url: `${ROOT}/arch-bg-anim.png`, cols: 4, rows: 3 },
      torch: { url: `${ROOT}/torch-36.png`, cols: 6, rows: 6 },
      kid: { url: `${ROOT}/kid.png`, cols: 4, rows: 4 },
      // Animated moon face - NightSky plays it once at random intervals.
      moon: { url: `${ROOT}/moon.png`, cols: 4, rows: 4 },
    },
  },
  mount(ctx) {
    // new battle scene -> drop leftover embed refs from the previous battle
    resetEmbeddedBalls();
    // Half-dark variant (debug NIGHT toggle): torches out, art dimmed cool,
    // moonlight through a wall hole. Read at mount - flips per battle. A
    // mid-battle wall break flips to night too (see enterNightMode).
    const night = isNightModeEnabled();
    // Night sky mounts FIRST (bottom-most) so it only shows through the hole
    // in the wall art laid over it.
    const nightSky = night ? buildNightSky(ctx) : null;
    if (nightSky) ctx.layer.addChild(nightSky);
    // Background composed of two same-sized layers; arena-bottom paints OVER
    // arena-top (foreground railing/floor on top of the back wall).
    const top = new Sprite(
      night ? (ctx.textures.arenaTopHole ?? ctx.textures.arenaTop) : ctx.textures.arenaTop,
    );
    top.width = DESIGN_WIDTH;
    top.height = DESIGN_HEIGHT;
    if (night) top.tint = NIGHT_ART_TINT;
    ctx.layer.addChild(top);

    const refs: NightRefs = {
      layer: ctx.layer,
      top,
      torches: [],
      tintables: [top],
      night,
      sky: nightSky,
    };
    nightRefs = refs;

    // Torches mounted on the back wall - between the two bg layers so the
    // foreground (arena-bottom) can occlude their lower portion if needed.
    // At night the same spots hold the extinguished sprite - the moon hole is
    // the light source instead.
    const torchFrames = ctx.sheets.torch;
    const torchOffTex = ctx.textures.torchOff;
    for (const cfg of TORCHES) {
      if (night) {
        if (!torchOffTex) break;
        ctx.layer.addChild(buildOffTorch(torchOffTex, cfg));
      } else if (torchFrames) {
        const torch = new Torch({
          frames: torchFrames,
          facing: cfg.facing,
          height: cfg.height,
        });
        torch.position.set(cfg.x, cfg.y);
        ctx.layer.addChild(torch);
        refs.torches.push({ view: torch, cfg });
      }
    }

    // Background seen through the arch opening - under the arch frame so the
    // stones cover its edges, over arena-top.
    const archBgFrames = ctx.sheets.archBg;
    const archBgFirst = archBgFrames?.[0];
    if (archBgFrames && archBgFirst) {
      const archBg = new AnimatedSprite(archBgFrames);
      archBg.anchor.set(0.5, 0);
      archBg.scale.set(ARCH_BG.height / archBgFirst.height);
      archBg.position.set(ARCH_BG.x, ARCH_BG.y);
      archBg.animationSpeed = ARCH_BG_ANIM_SPEED;
      archBg.loop = false;
      archBg.gotoAndStop(0);
      const scheduleArchBgPlay = (): void => {
        const delay =
          ARCH_BG_PAUSE_MIN_MS + Math.random() * (ARCH_BG_PAUSE_MAX_MS - ARCH_BG_PAUSE_MIN_MS);
        setTimeout(() => {
          if (!archBg.destroyed) archBg.gotoAndPlay(0);
        }, delay);
      };
      archBg.onComplete = () => {
        if (archBg.destroyed) return;
        archBg.gotoAndStop(0);
        scheduleArchBgPlay();
      };
      scheduleArchBgPlay();
      if (night) archBg.tint = NIGHT_ART_TINT;
      ctx.layer.addChild(archBg);
      refs.tintables.push(archBg);
    }

    // Doorway arch: over arena-top (and the torches' wall), under arena-bottom
    // so the foreground floor covers the pillar bases.
    const archTex = ctx.textures.arch;
    if (archTex) {
      const arch = new Sprite(archTex);
      arch.anchor.set(0.5, 0);
      arch.scale.set(ARCH.height / archTex.height);
      arch.position.set(ARCH.x, ARCH.y);
      if (night) arch.tint = NIGHT_ART_TINT;
      ctx.layer.addChild(arch);
      refs.tintables.push(arch);
    }

    const bottom = new Sprite(ctx.textures.arenaBottom);
    bottom.width = DESIGN_WIDTH;
    bottom.height = DESIGN_HEIGHT;
    if (night) bottom.tint = NIGHT_ART_TINT;
    ctx.layer.addChild(bottom);
    refs.tintables.push(bottom);

    // Beam + floor pool paint over the floor art (still behind the fighters).
    if (night) ctx.layer.addChild(buildMoonBeam(moonBobOffset));

    // Animated spectator on top of everything (added last = topmost). Peeks
    // over the foreground railing, hands gripping the edge.
    const kidFrames = ctx.sheets.kid;
    if (SHOW_KID && kidFrames && kidFrames.length > 0) {
      const kid = new Kid({ frames: kidFrames, height: KID.height });
      kid.position.set(KID.x, KID.y);
      if (night) kid.tint = NIGHT_ART_TINT;
      ctx.layer.addChild(kid);
      refs.tintables.push(kid);
    }

    // Foreground railings in the bottom corners, in front of the fighters.
    // Same texture both sides; the right one is mirrored (scale.x = -1 flips
    // the local x axis, so from the right edge it extends leftward).
    const railingTex = ctx.textures.railing;
    if (railingTex) {
      const scale = RAILING_HEIGHT / railingTex.height;
      const left = new Sprite(railingTex);
      left.anchor.set(0, 1);
      left.scale.set(scale);
      left.position.set(0, DESIGN_HEIGHT);
      const right = new Sprite(railingTex);
      right.anchor.set(0, 1);
      right.scale.set(-scale, scale);
      right.position.set(DESIGN_WIDTH, DESIGN_HEIGHT);
      if (night) {
        left.tint = NIGHT_ART_TINT;
        right.tint = NIGHT_ART_TINT;
      }
      ctx.foreground.addChild(left, right);
      refs.tintables.push(left, right);
    }
  },
  interactions: THE_PIT_INTERACTIONS,
  // Programmatic placeholder visuals (real sprites land later):
  //  attach  -> spawn the cannonball texture stuck in the upper wall above the
  //             blocking side, lightly tilted.
  //  release -> drop ball under the defender's feet, then play an additive
  //             expand-fade disc as a stand-in for the explosion.
  async playArenaEvent(event, ctx) {
    // Explosion rendezvous: wait for every dropped ball to touch down, then
    // bang them together. The renderer awaits this before playing the pain
    // reactions, so pain-air starts exactly at the BAM.
    if (event.type === 'arena_explode') {
      await Promise.all(
        (['p1', 'p2'] as const).map((side) => explodeDroppedBall(side, ctx)),
      );
      return;
    }
    if (event.objectId !== CANNONBALL_OBJECT_ID) return;
    // Second ball into the same wall: big BAM, all wall art on that side
    // (lodged ball + damage marks) is gone, and the night comes through the
    // fresh hole. The BAM covers the instant texture swap.
    if (event.type === 'arena_break') {
      breakWall(event.side, ctx);
      enterNightMode(event.side, ctx);
      return;
    }
    if (event.type === 'arena_attach') {
      const flightTex = ctx.textures.cannonball;
      if (!flightTex) return;
      // Second ricochet into an occupied wall: the ball never lodges - it
      // crashes and the wall blows the instant it connects, night comes on
      // right here (the engine's arena_break a few events later no-ops).
      // Only the skill's direct call (fromPosition) is a real new shot.
      if (event.fromPosition && hasEmbeddedBall(event.side)) {
        await crashBallIntoWall(event.side, flightTex, ctx, event.fromPosition);
        enterNightMode(event.side, ctx);
        return;
      }
      // fromPosition is set when a skill (e.g. cannon on block) calls this
      // directly at impact - the embed flies in from there. Engine-emitted
      // arena_attach has no fromPosition; spawnEmbeddedBall is idempotent so
      // the second call after the skill no-ops cleanly.
      await spawnEmbeddedBall(event.side, flightTex, ctx, {
        fromPosition: event.fromPosition,
        lodgedTexture: ctx.textures.cannonballInWall,
      });
      return;
    }
    if (event.type === 'arena_release') {
      await dropBall(event.side, ctx);
    }
  },
};
